import { Amount } from '@core/types/amount';
import { AkLocale } from '@core/types/base';
import { Option, Variant, VariantType, QualificationEnum } from '@monolith/legacy/types/product';
import { Brand } from '@monolith/legacy/types/api/brand';
import { CountryIsoCode } from '@core/types/country';
import { ItemIconConfig } from '@client/modules/discovery/ui/filters/types';

// TODO: these types are duplicates from the ones on the monolith
// remove/refactor the ones on the monolith once we start cleaning up all the
// files related to the old catalog/search

// TODO: these types are not aligned with the API responses.
// There's properties missing.
// We should update them to match the actual responses.

enum ESDiscountType {
  BRAND = 'brand_discount',
  EVENT_SPECIAL = 'event_special_discount',
  LOYALTY = 'loyalty_discount',
}

export type ESProductHitBrand = Pick<
  Brand,
  'id' | 'name' | 'link' | 'minimum' | 'opened_countries' | 'images' | 'type' | 'brand_tags' | 'uuid'
> & {
  country_code: CountryIsoCode;
  country: string;
  business_events: Record<
    number,
    {
      id: number;
      discount: number;
      ranking: number;
    }
  >;
};

type ESVariantOption = {
  display_name: string;
  id: number;
  name: VariantType;
  value: string;
};

export type ESVariant = {
  id: number;
  ian: string;
  options: ESVariantOption[];
  out_of_stock: boolean;
  retail_price: Amount;
  sku: string;
  stock: {
    id: number;
    available_quantity: number;
    is_always_in_stock: boolean;
    status: string;
  };
  uuid: string;
  wholesale_price: Amount;
  estimated_delivery?: {
    min_days: number | null;
    max_days: number | null;
  } | null;
};

export enum AksSystem {
  PROVEN_SUCCESS = 'proven_success',
  TRENDING_PRODUCT = 'trending_product',
  NEW_BRANDS = 'new_brands',
}

export type ESProductHit<TVariant = Variant> = {
  id: number;
  uuid: string;
  retail_price: Amount;
  wholesale_price: Amount;
  original_wholesale_price: Amount;
  discount_rate: number;
  discount_type?: ESDiscountType | null;
  unit_multiplier: number;
  tags: Record<AkLocale, string[]> | string[];
  link: string;
  name: Record<AkLocale, string> | string;
  display_name: Record<AkLocale, string> | string;
  description: Record<AkLocale, string> | string;
  brand: ESProductHitBrand;
  options: Option[];
  variants: TVariant[];
  images: string[];
  objectID: string;
  available_at?: number;
  estimated_delivery?: {
    min_days: number | null;
    max_days: number | null;
  };
  qualification?: QualificationEnum;
  is_new: boolean;
  is_ad: boolean;
  ad: {
    metadata: {
      ad_id: string;
      ad_type: string;
    };
  };
  aks_systems: AksSystem[];
};

enum AdType {
  PaidAd = 'paid_ad',
}

type AdMetadata = {
  ad_id: string;
  ad_type: AdType;
  ad_campaign_id: string;
};

export type ESSponsoredProductHit = ESProductHit & {
  // TODO: remove null alternative value once this BE bug is fixed
  aardvarkServeId?: string;
  ad: {
    metadata: AdMetadata | null;
  };
};

type MetaDataBrandHit = {
  last_order_date?: Date;
  sneak_peek?: boolean;
};

type BusinessEventId = number;

export type ESBrandHit = Pick<
  Brand,
  'id' | 'uuid' | 'name' | 'link' | 'images' | 'opened_countries' | 'active' | 'minimum' | 'brand_tags'
> & {
  has_new_products: boolean;
  featured_products: Record<
    string,
    {
      id: number;
      image: string;
    }
  >;
  metadata: MetaDataBrandHit;
  discount: number;
  shipping_lead_time: string[];
  business_events_eligible: BusinessEventId[];
  inserted_at: string;
  business_events: Record<
    BusinessEventId,
    {
      id: number;
      discount: number;
      ranking: number;
    }
  >;
};

export type AttributeType = 'group' | 'string' | 'number' | 'integer' | 'boolean' | 'color';

type AttributeChoice = {
  slug: string;
  name: string;
  data: null;
  position: number;
  iconConfig?: ItemIconConfig;
};

type AttributeChoiceColor = AttributeChoice & {
  data: null | { color: string };
};

type AttributeDefinitionBase = {
  type: AttributeType;
  slug: string;
  name: string;
  description: string;
  isMultiValued: boolean;
  attributes: AttributeDefinition[];
};

type AttributeDefinitionColor = AttributeDefinitionBase & {
  type: 'color';
  choices: AttributeChoiceColor[];
};

type AttributeDefinitionBoolean = AttributeDefinitionBase & {
  type: 'boolean';
};

type AttributeDefinitionString = AttributeDefinitionBase & {
  type: 'string';
  choices: AttributeChoice[];
};

type AttributeDefinitionNumber = AttributeDefinitionBase & {
  type: 'number';
};

type AttributeDefinitionInteger = AttributeDefinitionBase & {
  type: 'integer';
  unit: string;
  choices: AttributeChoice[];
};

export type AttributeDefinitionGroup = AttributeDefinitionBase & {
  type: 'group';
};

export type AttributeDefinition =
  | AttributeDefinitionGroup
  | AttributeDefinitionColor
  | AttributeDefinitionBoolean
  | AttributeDefinitionString
  | AttributeDefinitionNumber
  | AttributeDefinitionInteger;

/**
 * A map of labels to their desired labels.
 * This is useful for when the label is not user-friendly or must be translated.
 * For example, the label "2.0_2.5" could be mapped to "x2 - x2.5".
 *
 * @example
 * {
 *  '2.0_2.5': 'x2 - x2.5',
 *  '2.5_3.0': 'x2.5 - x3',
 * }
 */
export type LabelOverrides = Record<string, string>;
